import React, {useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import {ShopService} from '../services/shopService';
import ProductCard from '../components/ProductCard';
import FeaturedCard from '../components/FeaturedCard';
import CategoryTabs from '../components/CategoryTabs';
import SortDropdown, {type SortValue} from '../components/SortDropdown';

const ShopPage: React.FC = () => {
    const [category, setCategory] = useState<string | null>(null);
    const [sort, setSort] = useState<SortValue>('best_sellers');

    const {data, isLoading, error} = useQuery({
        queryKey: ['shop-products', category, sort],
        queryFn: () =>
            ShopService.getProducts({
                category: category ?? undefined,
                sort,
            }),
    });

    const products = data?.products ?? [];
    const categories = data?.categories ?? [];
    const total = data?.total ?? 0;

    // Insert the featured promo card after the first row (positions 0-3 = row 1, then promo, then rest)
    const firstRow = products.slice(0, 4);
    const remainingProducts = products.slice(4);

    return (
        <div>
            {/* Page header */}
            <h1 className="text-3xl md:text-4xl font-bold text-mld-text-primary mb-6">
                At-Home Testing & Treatments
            </h1>

            {/* Category tabs + sort/results count */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
                <CategoryTabs categories={categories} selected={category} onSelect={setCategory} />
                <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-500">{total} Results</span>
                    <SortDropdown value={sort} onChange={setSort} />
                </div>
            </div>

            {/* Loading state */}
            {isLoading && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {Array.from({length: 8}).map((_, i) => (
                        <div key={i} className="bg-mld-menu-background rounded-xl border border-mld-panel-border h-96 animate-pulse" />
                    ))}
                </div>
            )}

            {/* Error state */}
            {error && (
                <div className="text-center py-16">
                    <p className="text-gray-500">Unable to load products. Please try again.</p>
                </div>
            )}

            {/* Product grid */}
            {!isLoading && !error && (
                <>
                    {products.length === 0 ? (
                        <div className="text-center py-16">
                            <p className="text-gray-500 text-lg">No products found.</p>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {/* First row */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                                {firstRow.map((product) => (
                                    <ProductCard key={product.id} product={product} />
                                ))}
                            </div>

                            {/* Second row: 2 products + featured promo card */}
                            {remainingProducts.length > 0 && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                                    {remainingProducts.slice(0, 2).map((product) => (
                                        <ProductCard key={product.id} product={product} />
                                    ))}
                                    <FeaturedCard />
                                </div>
                            )}

                            {/* Remaining rows */}
                            {remainingProducts.length > 2 && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                                    {remainingProducts.slice(2).map((product) => (
                                        <ProductCard key={product.id} product={product} />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default ShopPage;
